/**
 * Download the catalog as a spreadsheet in the client's own format.
 *
 * The default (since 24 Oct 2026) is the format the client confirmed for both
 * directions, "MOLD LIST_63" ("Master Sheet"): the same 24 headings in the same
 * order, the same widths and look - so a file that comes out of MouldTrack reads
 * like theirs and can be filled in and imported straight back. The two layouts
 * used before - the Kandivali list (8 Oct) and "MOLD LIST LATEST" - stay
 * available as other choices.
 *
 * Every one of each mould's photos is embedded, in a grid in the IMAGE cell.
 * Built on the device from what Dexie already holds, so it works offline (with
 * each mould's main photo; the full galleries live on the server). The ~1MB of
 * ExcelJS is dynamically imported, which keeps it out of the bundle everyone
 * downloads and in a chunk only fetched when somebody exports.
 */
import type { MouldRow } from '../../db/queries.js';
import { whereabouts, type PlaceMap } from './useMouldLocations.js';
import { projectLabel } from './projectLabel.js';
import { MASTER_SHEET } from './masterSheet.js';
import { listAllPhotos } from '../../api/client.js';

export type ExportFormat = 'master-sheet' | 'kandivali' | 'mold-list-latest';

/** The format the client confirmed on 24 Oct 2026, for export and import alike. */
export const DEFAULT_EXPORT_FORMAT: ExportFormat = 'master-sheet';

type Value = string | number;

/** Where a mould is, for a location column. */
interface Place {
  /** The site it was last recorded at, on its own: "Mira Road". */
  location: string;
  /** As the catalog says it: the site, or "In transit · from Mira Road" while on the road. */
  where: string;
}

interface Column {
  /** The client's heading, verbatim. Do not "improve" these - they are the contract. */
  header: string;
  /** In Excel's character units, as the client's sheet has it. */
  width: number;
  value?: (m: MouldRow, index: number, place: Place) => Value;
  /** The column the photos sit in. */
  image?: true;
  align?: 'left' | 'center';
}

interface Layout {
  /** For the menu and the file name. */
  label: string;
  file: string;
  sheet: string;
  columns: Column[];
  header: { font: { name?: string; size?: number; bold?: boolean; italic?: boolean }; fill: string; height: number };
  body: { font?: { name?: string; size?: number; bold?: boolean }; minHeight: number; align: 'left' | 'center'; borders: boolean };
}

/** Only the mould's own name: blank when the sheet gave it none and it is just the code again. */
const ownName = (m: MouldRow): string => {
  const name = m.name?.trim() ?? '';
  return name && name.toUpperCase() !== (m.assetCode?.trim().toUpperCase() ?? '') ? name : '';
};

const yesNo = (v: boolean | null | undefined): string => (v == null ? '' : v ? 'YES' : 'NO');

/** "1251 - SNAP LOCK" in one cell, as MOLD LIST LATEST has it. */
const codeAndName = (m: MouldRow): string => {
  const name = ownName(m);
  return name ? `${m.assetCode} - ${name}` : m.assetCode;
};

const LAYOUTS: Record<ExportFormat, Layout> = {
  // "MOLD LIST_63", sheet "Master Sheet": the format the client confirmed on
  // 24 Oct 2026. Heading for heading, spelled exactly as their file spells them
  // ("NO.OF CAVITIES", "… AND ITS MESHANISM", "MOLD SIZE (LxBxH)"), at their
  // file's widths. Every heading imports back - CODE AND NAME as the project,
  // since 28 Oct 2026.
  'master-sheet': {
    label: 'Master Sheet format (MOLD LIST_63)',
    file: 'MOLD LIST',
    sheet: 'Master Sheet',
    // Headings from masterSheet.ts - the same words the catalog shows on screen.
    columns: [
      { header: MASTER_SHEET.sno, width: 14, value: (_m, i) => i + 1 },
      { header: MASTER_SHEET.owner, width: 26.22, value: (m) => m.ownerCompanyName ?? '' },
      { header: MASTER_SHEET.customer, width: 34.89, value: (m) => m.customerCompanyName ?? '' },
      { header: MASTER_SHEET.image, width: 76.44, image: true },
      { header: MASTER_SHEET.codeAndName, width: 77.89, value: projectLabel },
      { header: MASTER_SHEET.moldNumber, width: 46.89, value: (m) => m.assetCode },
      { header: MASTER_SHEET.moldName, width: 76.11, value: ownName },
      { header: MASTER_SHEET.toolType, width: 43.55, value: (m) => m.toolTypeName ?? '' },
      { header: MASTER_SHEET.cavities, width: 26, value: (m) => m.cavities ?? '' },
      { header: MASTER_SHEET.runnerType, width: 26.44, value: (m) => m.runnerTypeName ?? '' },
      { header: MASTER_SHEET.material, width: 26.33, value: (m) => m.materialGradeName ?? '' },
      { header: MASTER_SHEET.gateType, width: 29.44, value: (m) => m.gateTypeName ?? '' },
      { header: MASTER_SHEET.ejectionType, width: 23.44, value: (m) => m.ejectionTypeName ?? '' },
      { header: MASTER_SHEET.mechanism, width: 25.44, value: (m) => m.mechanismTypeName ?? '' },
      { header: MASTER_SHEET.coreCavityMaterial, width: 18.44, value: (m) => m.coreCavityMaterialName ?? '' },
      { header: MASTER_SHEET.moldBaseMaterial, width: 19.11, value: (m) => m.moldBaseMaterialName ?? '' },
      { header: MASTER_SHEET.moldSize, width: 35.55, value: (m) => m.moldSize ?? '' },
      { header: MASTER_SHEET.designer, width: 24.33, value: (m) => m.designerName ?? '' },
      { header: MASTER_SHEET.manufactureYear, width: 48.55, value: (m) => m.manufactureYear ?? '' },
      { header: MASTER_SHEET.manufacturer, width: 32.66, value: (m) => m.manufactureCompanyName ?? '' },
      { header: MASTER_SHEET.toolTransfer, width: 28.89, value: (m) => yesNo(m.toolTransfer) },
      { header: MASTER_SHEET.remarks, width: 28.89, value: (m) => m.description ?? '' },
      { header: MASTER_SHEET.qrNumber, width: 28.33, value: (m) => m.qrNumber ?? '' },
      { header: MASTER_SHEET.location, width: 28.33, value: (_m, _i, place) => place.where },
    ],
    // As their file: Calibri 18 bold italic headings on its grey (theme accent 3, #A5A5A5).
    header: { font: { name: 'Calibri', size: 18, bold: true, italic: true }, fill: 'FFA5A5A5', height: 114.75 },
    // Calibri 18 bold, centred, every cell boxed.
    body: { font: { name: 'Calibri', size: 18, bold: true }, minHeight: 48, align: 'center', borders: true },
  },
  // The client's Kandivali sheet ("Sheet1"), heading for heading - spelled as the
  // client listed them on 8 Oct 2026 ("NO. OF CAVITIES", "… AND ITS MECHANISM").
  // The default until 24 Oct 2026.
  kandivali: {
    label: 'Kandivali mould list format (before 24 Oct)',
    file: 'MOLD LIST',
    sheet: 'Sheet1',
    columns: [
      { header: 'S.NO', width: 7.71, value: (_m, i) => i + 1 },
      { header: 'Mould Ownership', width: 26.86, value: (m) => m.ownerCompanyName ?? '' },
      { header: 'Customer', width: 15.71, value: (m) => m.customerCompanyName ?? '' },
      { header: 'PROJECT CODE AND NAME', width: 32.86, value: projectLabel },
      { header: 'IMAGE', width: 32.86, image: true },
      { header: 'MOLD NUMBER', width: 24.14, value: (m) => m.assetCode },
      { header: 'MOLD NAME', width: 98.86, value: ownName, align: 'left' },
      { header: 'TOOL TYPE', width: 27.14, value: (m) => m.toolTypeName ?? '' },
      { header: 'NO. OF CAVITIES', width: 21.71, value: (m) => m.cavities ?? '' },
      { header: 'RUNNER TYPE', width: 23.14, value: (m) => m.runnerTypeName ?? '' },
      { header: 'MATERIAL-SHRINKAGE', width: 34, value: (m) => m.materialGradeName ?? '' },
      { header: 'GATE TYPE', width: 17.14, value: (m) => m.gateTypeName ?? '' },
      { header: 'EJECTION TYPE', width: 24.43, value: (m) => m.ejectionTypeName ?? '' },
      { header: 'NO. OF SLIDERS AND LIFTERS AND ITS MECHANISM', width: 41, value: (m) => m.mechanismTypeName ?? '' },
      { header: 'CORE CAVITY MATERIAL', width: 35.71, value: (m) => m.coreCavityMaterialName ?? '' },
      { header: 'MOLD BASE MATERIAL', width: 35.29, value: (m) => m.moldBaseMaterialName ?? '' },
      { header: 'MOLD SIZE', width: 38.14, value: (m) => m.moldSize ?? '' },
      { header: 'DESIGNER NAME', width: 27.29, value: (m) => m.designerName ?? '' },
      { header: 'MANUFACTURE/TRANSFERRED YEAR', width: 46.14, value: (m) => m.manufactureYear ?? '' },
      { header: 'MANUFACTURER', width: 27.57, value: (m) => m.manufactureCompanyName ?? '' },
      { header: 'TOOL TRANSFER', width: 24.86, value: (m) => yesNo(m.toolTransfer) },
      { header: 'REMARKS', width: 15.86, value: (m) => m.description ?? '' },
      { header: 'QR NUMBER', width: 21, value: (m) => m.qrNumber ?? '' },
    ],
    // As the sheet: Calibri 14 bold italic on its grey (theme accent 3, #A5A5A5).
    header: { font: { name: 'Calibri', size: 14, bold: true, italic: true }, fill: 'FFA5A5A5', height: 41.25 },
    // Arial 12, centred, every cell boxed, 30-point rows - taller where photos sit.
    body: { font: { name: 'Arial', size: 12 }, minHeight: 30, align: 'center', borders: true },
  },
  // "MOLD LIST LATEST", the layout exported before 8 Oct 2026 - with location.
  'mold-list-latest': {
    label: 'MOLD LIST LATEST format (with location)',
    file: 'mould-list',
    sheet: 'MOLD LIST',
    columns: [
      { header: 'S.NO', width: 6, value: (_m, i) => i + 1 },
      { header: 'IMAGE', width: 16, image: true },
      { header: 'PROJECT CODE AND NAME', width: 26, value: projectLabel },
      { header: 'MOLD CODE AND NAME', width: 30, value: codeAndName },
      { header: 'TOOL TYPE', width: 14, value: (m) => m.toolTypeName ?? '' },
      { header: 'NO.OF CAVITIES', width: 14, value: (m) => m.cavities ?? '' },
      { header: 'MATERIAL-SHRINKAGE', width: 20, value: (m) => m.materialGradeName ?? '' },
      { header: 'RUNNER TYPE', width: 18, value: (m) => m.runnerTypeName ?? '' },
      { header: 'GATE TYPE', width: 20, value: (m) => m.gateTypeName ?? '' },
      { header: 'EJECTION TYPE', width: 16, value: (m) => m.ejectionTypeName ?? '' },
      { header: 'NO.OF SLIDERS AND LIFTERS AND ITS MESHANISM', width: 26, value: (m) => m.mechanismTypeName ?? '' },
      { header: 'CORE CAVITY MATERIAL', width: 20, value: (m) => m.coreCavityMaterialName ?? '' },
      { header: 'MOLD BASE MATERIAL', width: 20, value: (m) => m.moldBaseMaterialName ?? '' },
      { header: 'MOLD SIZE', width: 22, value: (m) => m.moldSize ?? '' },
      { header: 'DESIGNER NAME', width: 16, value: (m) => m.designerName ?? '' },
      { header: 'MANUFACTURE YEAR', width: 12, value: (m) => m.manufactureYear ?? '' },
      { header: 'MANUFACTURE COMPANY', width: 20, value: (m) => m.manufactureCompanyName ?? '' },
      { header: 'MOLD OWNER', width: 16, value: (m) => m.ownerCompanyName ?? '' },
      { header: 'CUSTOMER', width: 16, value: (m) => m.customerCompanyName ?? '' },
      { header: 'MOLD LOCATION', width: 18, value: (_m, _i, place) => place.location },
      { header: 'QR NUMBER', width: 16, value: (m) => m.qrNumber ?? '' },
      { header: 'TOOL TRANSFER', width: 12, value: (m) => yesNo(m.toolTransfer) },
      { header: 'REMARKS', width: 30, value: (m) => m.description ?? '' },
    ],
    header: { font: { bold: true }, fill: 'FFD9E1F2', height: 34 },
    body: { minHeight: 78, align: 'left', borders: false },
  },
};

export const EXPORT_FORMATS = (Object.keys(LAYOUTS) as ExportFormat[]).map((id) => ({ id, label: LAYOUTS[id].label }));

/** The headings of a format, in order - for a blank template to fill in. */
export function formatHeaders(format: ExportFormat = DEFAULT_EXPORT_FORMAT): string[] {
  return LAYOUTS[format].columns.map((c) => c.header);
}

/**
 * How big a picture actually is, read out of its own header.
 *
 * Needed because the cell is a fixed box and the photos are not a fixed shape:
 * without the real proportions every picture gets stretched to the box, and a
 * tall tool comes out looking like a wide one. Three formats, three headers,
 * all of them in the first few dozen bytes - no decoding required.
 */
function imagePixelSize(base64: string, extension: string): { width: number; height: number } | null {
  let bytes: Uint8Array;
  try {
    const bin = atob(base64.slice(0, 2048));
    bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
  } catch {
    return null;
  }
  const be32 = (o: number) =>
    ((bytes[o]! << 24) | (bytes[o + 1]! << 16) | (bytes[o + 2]! << 8) | bytes[o + 3]!) >>> 0;
  const be16 = (o: number) => (bytes[o]! << 8) | bytes[o + 1]!;

  if (extension === 'png') {
    // IHDR is always the first chunk: width and height at bytes 16 and 20.
    if (bytes.length < 24) return null;
    return { width: be32(16), height: be32(20) };
  }
  if (extension === 'gif') {
    if (bytes.length < 10) return null;
    return { width: bytes[6]! | (bytes[7]! << 8), height: bytes[8]! | (bytes[9]! << 8) };
  }
  // JPEG: walk the markers to the start-of-frame, which carries the size.
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) { i++; continue; }
    const marker = bytes[i + 1]!;
    // SOF0..SOF15, minus the three that are not frame headers.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { height: be16(i + 5), width: be16(i + 7) };
    }
    i += 2 + be16(i + 2);
  }
  return null;
}

/** One picture as it goes into the workbook, with its size in pixels. */
interface SheetImage {
  base64: string;
  extension: 'png' | 'jpeg' | 'gif';
  width: number;
  height: number;
}

/** Each thumbnail's box in the IMAGE column, in pixels, and the gap around it. */
const THUMB = { width: 100, height: 84 };
const GAP = 4;
/** Excel will not make a row taller than 409 points - 545 pixels. */
const MAX_ROW_PX = 520;

/** How many thumbnails sit side by side for a mould with `n` pictures. */
function perRowFor(n: number): number {
  return n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4;
}

/**
 * The grid one mould's pictures are laid out in. Shrinks the thumbnails, and
 * fits more to a line, only when there are so many that the row would pass
 * Excel's height limit - every picture goes in, none is dropped to fit.
 */
function gridFor(n: number, colWidthPx: number) {
  const fitAcross = (w: number) => Math.max(1, Math.floor((colWidthPx - GAP) / (w + GAP)));
  let box = { ...THUMB };
  let per = Math.min(perRowFor(n), fitAcross(box.width));
  while (Math.ceil(n / per) * (box.height + GAP) + GAP > MAX_ROW_PX && box.height > 16) {
    box = { width: Math.round(box.width * 0.9), height: Math.round(box.height * 0.9) };
    per = Math.max(per, Math.min(n, fitAcross(box.width)));
  }
  const lines = Math.ceil(n / per);
  return { per, box, heightPx: n ? lines * (box.height + GAP) + GAP : 0 };
}

export interface ExportOptions {
  rows: MouldRow[];
  places: PlaceMap;
  /** The client's Master Sheet layout unless another is asked for. */
  format?: ExportFormat;
  /** Appended to the filename, e.g. "filtered". */
  label?: string;
  /** Called as pictures arrive, for a progress line on a large catalog. */
  onProgress?: (done: number, total: number) => void;
}

export interface ExportResult {
  moulds: number;
  /** Pictures put in the file. */
  photos: number;
  /** Pictures a mould has that could not be put in the file, and why. */
  missed: Array<{ code: string; reason: string }>;
  /**
   * The full galleries could not be fetched - offline - so each mould went in
   * with the one photo the device keeps for it.
   */
  primaryOnly: boolean;
}

const THIN = { style: 'thin' } as const;
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };

/** Builds the workbook and hands it to the browser as a download. */
export async function exportMouldsToExcel({
  rows,
  places,
  format = DEFAULT_EXPORT_FORMAT,
  label,
  onProgress,
}: ExportOptions): Promise<ExportResult> {
  const layout = LAYOUTS[format];
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'MouldTrack';
  wb.created = new Date();
  const ws = wb.addWorksheet(layout.sheet);

  const header = ws.addRow(layout.columns.map((c) => c.header));
  header.height = layout.header.height;
  header.eachCell((cell) => {
    cell.font = layout.header.font;
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: layout.header.fill } };
    cell.border = BOX;
  });

  const { galleries, primaryOnly } = await loadGalleries(rows);
  const pictures = await loadPictures(
    rows.flatMap((m) => galleries.get(m.id) ?? []),
    onProgress,
  );

  // The IMAGE column is as wide as the layout has it, or the widest grid any mould needs.
  const imageCol = layout.columns.findIndex((c) => c.image);
  const widest = rows.reduce((w, m) => Math.max(w, perRowFor(galleries.get(m.id)?.length ?? 0)), 1);
  const gridPx = widest * (THUMB.width + GAP) + GAP;
  layout.columns.forEach((c, i) => {
    ws.getColumn(i + 1).width = i === imageCol ? Math.max(c.width, (gridPx - 5) / 7) : c.width;
  });
  const colWidthPx = (ws.getColumn(imageCol + 1).width ?? 16) * 7 + 5;
  // Headings stay put while someone scrolls 600 rows of moulds.
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  const result: ExportResult = { moulds: rows.length, photos: 0, missed: [], primaryOnly };

  rows.forEach((m, i) => {
    const known = places[m.assetCode?.trim().toUpperCase() ?? ''];
    const location = known?.location ?? '';
    // While a mould is on the road its last scan is the site it left, so the
    // site on its own would be wrong: say it the way the catalog does.
    const where = whereabouts(m, known);
    const place: Place = { location, where: where.inTransit ? (where.label ?? 'In transit') : location };
    // The row's place in this list, from 1 - never the catalog number or an id.
    const values = layout.columns.map((c) => (c.value ? c.value(m, i, place) : ''));
    const row = ws.addRow(values);
    layout.columns.forEach((c, k) => {
      const cell = row.getCell(k + 1);
      if (layout.body.font) cell.font = layout.body.font;
      cell.alignment = { vertical: 'middle', horizontal: c.align ?? layout.body.align, wrapText: true };
      if (layout.body.borders) cell.border = BOX;
    });

    const urls = galleries.get(m.id) ?? [];
    const ready: Array<{ url: string; img: SheetImage }> = [];
    const reasons = new Map<string, number>();
    for (const url of urls) {
      const got = pictures.get(url);
      if (got && typeof got !== 'string') ready.push({ url, img: got });
      else reasons.set(got ?? 'not downloaded', (reasons.get(got ?? 'not downloaded') ?? 0) + 1);
    }
    for (const [reason, count] of reasons) {
      result.missed.push({ code: m.assetCode, reason: count > 1 ? `${count} photos: ${reason}` : reason });
    }

    // Say so in the cell, under the pictures, rather than leave one out quietly.
    const note = reasons.size
      ? `${urls.length - ready.length} of ${urls.length} photo${urls.length === 1 ? '' : 's'} could not be included`
      : '';
    const grid = gridFor(ready.length, colWidthPx);
    const notePx = note ? 30 : 0;
    // Excel measures row height in points and pictures in pixels; 4 points to
    // every 3 pixels. Never shorter than the layout's own rows, nor than its
    // longest wrapped text needs.
    row.height = Math.min(
      409,
      Math.max(layout.body.minHeight, ((grid.heightPx + notePx) * 3) / 4 + 2, textHeight(values, layout)),
    );
    if (note) {
      const cell = row.getCell(imageCol + 1);
      cell.value = note;
      cell.alignment = { vertical: 'bottom', horizontal: 'center', wrapText: true };
      cell.font = { italic: true, size: 8, color: { argb: 'FF9C2B2B' } };
    }

    /*
     * Sized in pixels, not as a fraction of the cell.
     *
     * The fractional form reads so much better that it survived review, and
     * it is quietly broken: ExcelJS turns a fractional column offset into
     * EMU by multiplying the column width by a flat ten thousand, which is
     * right for nothing. A width-16 column is about a million EMU, so every
     * photo was being anchored into a box eleven pixels wide and stretched
     * to fill it - the thin smears people saw down the IMAGE column.
     *
     * An explicit extent in pixels goes through a different path that does
     * not guess, and scaling to fit keeps a tall tool looking tall. Offsets
     * are in EMU: 9525 to the pixel.
     */
    const EMU = 9525;
    const across = Math.min(ready.length, grid.per);
    const gridWidth = across * (grid.box.width + GAP) + GAP;
    const left = Math.max(0, (colWidthPx - gridWidth) / 2);
    ready.forEach(({ img }, k) => {
      /*
       * A copy per placement, even for a picture two moulds share.
       *
       * Placing one ExcelJS image on two rows crosses its picture references:
       * a later row then points at the shared picture instead of its own (the
       * file opened in Excel shows the wrong photo, and importing it back
       * gives that mould a stranger's photo). Seen on 8 Oct 2026, 7 rows of 687.
       */
      const id = wb.addImage({ base64: img.base64, extension: img.extension });
      const scale = Math.min(grid.box.width / img.width, grid.box.height / img.height, 1);
      const size = { width: Math.max(1, Math.round(img.width * scale)), height: Math.max(1, Math.round(img.height * scale)) };
      const x = left + GAP + (k % grid.per) * (grid.box.width + GAP) + (grid.box.width - size.width) / 2;
      const y = GAP + Math.floor(k / grid.per) * (grid.box.height + GAP) + (grid.box.height - size.height) / 2;
      ws.addImage(id, {
        tl: {
          nativeCol: imageCol,
          nativeColOff: Math.max(0, Math.round(x * EMU)),
          nativeRow: row.number - 1,
          nativeRowOff: Math.max(0, Math.round(y * EMU)),
        },
        ext: size,
        editAs: 'oneCell',
      } as unknown as Parameters<typeof ws.addImage>[1]);
      result.photos++;
    });
  });

  const stamp = new Date().toISOString().slice(0, 10);
  const suffix = label ? `-${label}` : '';
  const buffer = await wb.xlsx.writeBuffer();
  saveFile(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${layout.file}${suffix}-${stamp}.xlsx`,
  );
  return result;
}

/**
 * The points a row needs for its longest wrapped cell.
 *
 * A row whose height is set does not grow in Excel, so a long name in a narrow
 * column would be cut off - more so at the Master Sheet's 18-point bold. An
 * estimate from how many characters fit across each column at the layout's
 * font, a little generous because words do not break mid-word.
 */
function textHeight(values: Value[], layout: Layout): number {
  const size = layout.body.font?.size ?? 11;
  // A column's width counts characters of the default 11-point font; bold is wider.
  const charWidth = (size / 11) * (layout.body.font?.bold ? 1.15 : 1);
  let lines = 1;
  values.forEach((v, i) => {
    const text = String(v ?? '').trim();
    const column = layout.columns[i];
    if (!text || !column || column.image) return;
    const fit = Math.max(1, Math.floor(((column.width - 1) / charWidth) * 0.85));
    lines = Math.max(lines, Math.ceil(text.length / fit));
  });
  return lines * size * 1.25 + 8;
}

/**
 * A blank sheet in the client's format, to fill in and import: the headings,
 * the widths, the look - no rows.
 */
export async function downloadImportTemplate(format: ExportFormat = DEFAULT_EXPORT_FORMAT): Promise<void> {
  const layout = LAYOUTS[format];
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'MouldTrack';
  const ws = wb.addWorksheet(layout.sheet);
  const header = ws.addRow(layout.columns.map((c) => c.header));
  header.height = layout.header.height;
  header.eachCell((cell) => {
    cell.font = layout.header.font;
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: layout.header.fill } };
    cell.border = BOX;
  });
  layout.columns.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.width;
  });
  ws.views = [{ state: 'frozen', ySplit: 1 }];
  const buffer = await wb.xlsx.writeBuffer();
  saveFile(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${layout.file} - template.xlsx`,
  );
}

/**
 * Every picture of every mould, in gallery order, by mould id.
 *
 * One request for the lot rather than one per mould. Offline the galleries
 * cannot be had - they are not replicated to devices - so each mould falls
 * back to the one photo the device keeps for it, and the caller is told.
 */
async function loadGalleries(rows: MouldRow[]): Promise<{ galleries: Map<string, string[]>; primaryOnly: boolean }> {
  const galleries = new Map<string, string[]>();
  let primaryOnly = false;
  try {
    for (const p of await listAllPhotos()) {
      const list = galleries.get(p.mouldId);
      if (list) list.push(p.url);
      else galleries.set(p.mouldId, [p.url]);
    }
  } catch {
    primaryOnly = true;
  }
  for (const m of rows) {
    // A photo taken offline and not yet uploaded is still on the record.
    if (!galleries.has(m.id) && m.photoUrl) galleries.set(m.id, [m.photoUrl]);
  }
  return { galleries, primaryOnly };
}

/**
 * Each picture, fetched once however many moulds show it, ready for the
 * workbook - or the reason it could not be.
 *
 * From the browser's cache when it has been seen before, since photo files
 * never change.
 */
async function loadPictures(
  urls: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<Map<string, SheetImage | string>> {
  const unique = [...new Set(urls)];
  const out = new Map<string, SheetImage | string>();
  let next = 0;
  let done = 0;
  onProgress?.(0, unique.length);
  const worker = async () => {
    while (next < unique.length) {
      const url = unique[next++]!;
      out.set(url, await loadPicture(url));
      onProgress?.(++done, unique.length);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  return out;
}

async function loadPicture(url: string): Promise<SheetImage | string> {
  let blob: Blob;
  try {
    // fetch() reads a data: URL as readily as a file on the server.
    const res = await fetch(url);
    if (!res.ok) return res.status === 404 ? 'the photo file is missing on the server' : `the server answered ${res.status}`;
    blob = await res.blob();
  } catch {
    return typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'could not be downloaded';
  }

  // PNG, JPEG and GIF go in exactly as stored. The same bytes as the catalog's
  // own file means an export filled in and imported back recognises every
  // photo it already has, instead of adding a second copy of each.
  const extension = /png/i.test(blob.type) ? 'png' : /gif/i.test(blob.type) ? 'gif' : /jpe?g/i.test(blob.type) ? 'jpeg' : null;
  if (extension) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    for (let b = 0; b < bytes.length; b += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(b, b + 0x8000));
    }
    const base64 = btoa(binary);
    const size = imagePixelSize(base64, extension) ?? (await decodedSize(blob)) ?? THUMB;
    return { base64, extension, ...size };
  }

  // WebP and BMP: an .xlsx cannot hold them, so they are drawn as JPEG rather
  // than left out.
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return `a ${blob.type || 'unknown'} picture this device cannot convert`;
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return 'the picture could not be read';
  }
  try {
    const { width, height } = bitmap;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'the picture could not be converted';
    ctx.fillStyle = '#ffffff'; // a transparent picture would otherwise go black
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    return { base64: dataUrl.slice(dataUrl.indexOf(',') + 1), extension: 'jpeg', width, height };
  } catch {
    return 'the picture could not be converted';
  } finally {
    bitmap.close();
  }
}

/** A picture's size by decoding it - for a JPEG whose size is not in its first bytes. */
async function decodedSize(blob: Blob): Promise<{ width: number; height: number } | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(blob);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

function saveFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
